// Les choix du prologue, du cadeau de Suzanne jusqu'au projet de vie.
import { HOUSING, MILIEUX, PATHS, PROJECTS } from '../content/paths';
import { round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import { t } from '../engine/i18n';
import type { GameState, HousingId, PathId, ProjectId } from '../engine/state';
import { recordLife } from '../engine/compteur';
import { DAYS_PER_YEAR, isValidISODate, startOfLife } from '../engine/time';
import { addJournal, unlockPage } from './actions';
import { startFirstJob } from './progress';
import { energyMax } from './work';
import { MUTUELLE_PRICES } from './health';
import { connectInternet } from './housing';
import { startSubscriptions } from './subscriptions';

export const DEFAULT_NAME = 'Camille';

export function openGift(state: GameState, name: string, birthDate?: string): void {
  if (state.prologueStep !== 'gift') return;
  state.name = name.trim() || DEFAULT_NAME;
  // La vie de jeu commence l'été des 18 ans. Rien n'a encore eu lieu : on peut déplacer le calendrier.
  if (birthDate && isValidISODate(birthDate)) {
    state.birthDate = birthDate;
    state.startDate = startOfLife(birthDate);
  }
  unlockPage(state, 'premiere_page');
  addJournal(state, 'journal.gift');
  state.prologueStep = 'summer';
  // Une vie de plus au compteur : celui du navigateur, et le mondial s'il est déployé.
  recordLife();
}

export function finishSummer(state: GameState): void {
  if (state.prologueStep !== 'endSummer') return;
  state.energyUnlocked = true;
  state.energy = energyMax(state);
  state.prologueStep = 'path';
  // Le milieu familial, affiché avec le choix du chemin, reste au journal pour s'en souvenir.
  addJournal(state, 'journal.milieu', { milieu: t(MILIEUX[state.milieu].text) });
}

export function choosePath(state: GameState, path: PathId): void {
  if (state.prologueStep !== 'path') return;
  state.path = path;
  addJournal(state, PATHS[path].journal);
  state.prologueStep = 'housing';
}

export function chooseHousing(state: GameState, housing: HousingId): void {
  if (state.prologueStep !== 'housing') return;
  const def = HOUSING[housing];
  const working = state.path === 'work';
  state.housing = housing;
  const expense = (amount: number) => ({ amount, examined: 0, optimized: false });
  state.expenses = {
    logement: expense(def.rent),
    ...(def.insurance > 0 ? { assurance: expense(def.insurance) } : {}),
    ...(def.charges > 0 ? { charges: expense(def.charges) } : {}),
    ...(def.energy > 0 ? { energie: expense(def.energy) } : {}),
    courses: expense(working ? 250 : 180),
    personnel: expense(working ? 90 : 50),
    transport: expense(working ? 60 : 30),
    forfait: expense(20),
    banque: expense(8),
    mutuelle: expense(working ? MUTUELLE_PRICES.employee.base : MUTUELLE_PRICES.student.base),
  };
  // Les abonnements du départ font la ligne « Abonnements » : 35 € par mois, plus la box internet hors de chez les parents.
  startSubscriptions(state);
  connectInternet(state, housing);
  if (housing !== 'parents') state.flags = { ...state.flags, fraisHabitation: true };
  addJournal(state, def.journal);
  state.prologueStep = 'account';
}

export function chooseOverdraft(state: GameState, accept: boolean): void {
  if (state.prologueStep !== 'account') return;
  state.overdraftAccepted = accept;
  addJournal(state, accept ? 'journal.overdraft.accepted' : 'journal.overdraft.refused');
  state.flags.livretOpen = true;
  addJournal(state, 'journal.livretOpen');
  const gift = MILIEUX[state.milieu].startingGift;
  if (gift > 0) {
    state.cash = round2(state.cash + gift);
    addJournal(state, 'journal.parentsGift', { amount: fmtEur(gift) });
  }
  state.prologueStep = 'project';
}

export function chooseProject(state: GameState, project: ProjectId): void {
  if (state.prologueStep !== 'project') return;
  state.project = project;
  addJournal(state, 'journal.project', { project: t(PROJECTS[project].title) });
  const path = PATHS[state.path ?? 'work'];
  state.budget.leisure = path.leisure;
  state.prologueStep = 'years';
  state.lastEventDay = state.day;
  state.interfaceDay = state.day;
  if (path.yearsBeforeJob === 0) {
    startFirstJob(state);
    return;
  }
  state.firstJobTargetDay = state.day + path.yearsBeforeJob * DAYS_PER_YEAR;
  state.studentIncome = path.studentIncome;
  state.parentsHelp = path.parentsHelp[state.milieu];
}
